import React, { useState, useEffect } from 'react';
import EventNoteIcon from '@mui/icons-material/EventNote';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
// Appointment type icons
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import EmergencyIcon from '@mui/icons-material/Emergency';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import LoopIcon from '@mui/icons-material/Loop';
import HotelIcon from '@mui/icons-material/Hotel';
import EventIcon from '@mui/icons-material/Event';
import ForwardIcon from '@mui/icons-material/Forward';
import VideocamIcon from '@mui/icons-material/Videocam';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import ForumIcon from '@mui/icons-material/Forum';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { APPOINTMENT_TYPE_ICONS, APPOINTMENT_TYPES, APPOINTMENT_STATUSES } from '../../types/enums';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Tooltip
} from '@mui/material';

const communicationMethods = [
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'portal', label: 'Patient Portal' },
];

const appointmentTypeOptions = APPOINTMENT_TYPES;
const statusOptions = APPOINTMENT_STATUSES;

const AppointmentDetails = ({ appointment, open, onClose, onCreateEncounter, onUpdate, editMode = false }) => {
  const [edit, setEdit] = useState(editMode);
  const [edited, setEdited] = useState(appointment);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Helper function to render appointment type icon
  const renderAppointmentTypeIcon = (appointmentType) => {
    const iconMap = {
      'DirectionsWalk': DirectionsWalkIcon,
      'Emergency': EmergencyIcon,
      'PriorityHigh': PriorityHighIcon,
      'Loop': LoopIcon,
      'Hotel': HotelIcon,
      'Event': EventIcon,
      'Forward': ForwardIcon,
      'Videocam': VideocamIcon,
      'SwapHoriz': SwapHorizIcon,
      'Visibility': VisibilityIcon,
      'Vaccines': VaccinesIcon,
      'PersonAdd': PersonAddIcon,
      'MedicalServices': MedicalServicesIcon,
      'Forum': ForumIcon,
      'Psychology': PsychologyIcon,
    };

    const iconName = APPOINTMENT_TYPE_ICONS[appointmentType];
    const IconComponent = iconMap[iconName] || EventNoteIcon; // Fallback to EventNoteIcon

    return <IconComponent sx={{ fontSize: 18, color: '#1976d2' }} />;
  };

  useEffect(() => {
    setEdit(editMode);
  }, [editMode, appointment]);

  // Guard clause to prevent errors if appointment is null
  if (!appointment) return null;

  const handleField = (field, value) => {
    if (field === 'date' && value instanceof Date) {
      // Store dates as ISO strings to ensure serializability
      setEdited(prev => ({
        ...prev,
        [field]: isNaN(value.getTime()) ? '' : value.toISOString()
      }));
    } else {
      setEdited(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSave = async () => {
    if (onUpdate) {
      setLoading(true);
      setError('');
      try {
        await onUpdate(edited);
        setEdit(false);
      } catch (err) {
        setError('Failed to update appointment. Please try again.');
        console.error('Error updating appointment:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = () => {
    setEdit(true);
    setEdited(appointment);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Appointment Details</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {edit ? (
                <TextField
                  label="Patient Name"
                  value={edited.patientName || ''}
                  onChange={e => handleField('patientName', e.target.value)}
                  size="small"
                  fullWidth
                />
              ) : (
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{appointment.patientName}</Typography>
              )}
              <Tooltip title={`Type: ${appointment.appointmentType || ''}`} placement="top">
                {renderAppointmentTypeIcon(appointment.appointmentType)}
              </Tooltip>
              {appointment.isFirstInFacility && (
                <Tooltip title="New Patient (First Appointment in Facility)" placement="top">
                  <FiberNewIcon sx={{ fontSize: 18, color: '#43a047' }} />
                </Tooltip>
              )}
              {appointment.intakeFormsDue && (
                <Tooltip title="Intake Forms Due" placement="top">
                  <AssignmentLateIcon sx={{ fontSize: 18, color: '#fbc02d' }} />
                </Tooltip>
              )}
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Date & Time</Typography>
            {edit ? (
              <TextField
                type="datetime-local"
                value={(() => {
                  if (!edited.date) return '';
                  let d = edited.date instanceof Date ? edited.date : new Date(edited.date);
                  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0,16);
                })()}
                onChange={e => handleField('date', new Date(e.target.value))}
                size="small"
                fullWidth
              />
            ) : (
              <Typography>{new Date(appointment.date).toLocaleString()}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Status</Typography>
            {edit ? (
              <Select
                value={edited.status}
                onChange={e => handleField('status', e.target.value)}
                size="small"
                fullWidth
                inputProps={{
                  'aria-hidden': false,
                  tabIndex: 0
                }}
              >
                {statusOptions.map(opt => (
                  <MenuItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</MenuItem>
                ))}
              </Select>
            ) : (
              <Chip label={appointment.status} color="primary" size="small" />
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Doctor</Typography>
            {edit ? (
              <TextField value={edited.doctorName || ''} onChange={e => handleField('doctorName', e.target.value)} size="small" fullWidth />
            ) : (
              <Typography>{appointment.doctorName}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Facility</Typography>
            {edit ? (
              <TextField value={edited.facilityName || ''} onChange={e => handleField('facilityName', e.target.value)} size="small" fullWidth />
            ) : (
              <Typography>{appointment.facilityName}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Appointment Type</Typography>
            {edit ? (
              <FormControl fullWidth size="small">
                <InputLabel id="appointment-type-label">Appointment Type</InputLabel>
                <Select
                  labelId="appointment-type-label"
                  value={edited.appointmentType || ''}
                  onChange={e => handleField('appointmentType', e.target.value)}
                  label="Appointment Type"
                  inputProps={{
                    'aria-hidden': false,
                    tabIndex: 0
                  }}
                >
                  {appointmentTypeOptions.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography>{appointment.appointmentType}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Purpose</Typography>
            {edit ? (
              <TextField value={edited.purpose || ''} onChange={e => handleField('purpose', e.target.value)} size="small" fullWidth />
            ) : (
              <Typography>{appointment.purpose}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Phone</Typography>
            {edit ? (
              <TextField value={edited.phone || ''} onChange={e => handleField('phone', e.target.value)} size="small" fullWidth />
            ) : (
              <Typography>{appointment.phone}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Email</Typography>
            {edit ? (
              <TextField value={edited.email || ''} onChange={e => handleField('email', e.target.value)} size="small" fullWidth />
            ) : (
              <Typography>{appointment.email}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Communication Method</Typography>
            {edit ? (
              <FormControl fullWidth size="small">
                <InputLabel id="communication-method-label">Communication Method</InputLabel>
                <Select
                  labelId="communication-method-label"
                  value={edited.communicationMethod || ''}
                  onChange={e => handleField('communicationMethod', e.target.value)}
                  label="Communication Method"
                  inputProps={{
                    'aria-hidden': false,
                    tabIndex: 0
                  }}
                >
                  {communicationMethods.map(method => (
                    <MenuItem key={method.value} value={method.value}>{method.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography>{appointment.communicationMethod}</Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2">Notes</Typography>
            {edit ? (
              <TextField value={edited.notes || ''} onChange={e => handleField('notes', e.target.value)} size="small" fullWidth multiline rows={2} />
            ) : (
              <Typography>{appointment.notes}</Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      {error && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </Box>
      )}
      <DialogActions>
        {edit ? (
          <>
            <Button 
              onClick={handleSave} 
              color="primary" 
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
            <Button onClick={onClose} color="secondary" disabled={loading}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => onCreateEncounter && onCreateEncounter(appointment)} color="primary" variant="contained">Create Encounter</Button>
            <Button onClick={onClose} color="secondary">Close</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentDetails;
